import * as React from 'react';
import { ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { generateImageUrl, getFallbackImageUrl, OrderTemplate } from '@msdyn365-commerce-modules/retail-actions';
import { BuyValidationResult, IProductListResources, IProductSelection, ProductListProduct } from '../product-list-component/product-list-component';
import { IProductError, SimpleProductExt } from '../../shared-utils/types';
import { CommerceList, Customer, format, ProductList, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import PQueue from 'p-queue';
import { ValidateProductAccessResult } from '../../shared-utils/validate-product-access';
import { getLocale } from '../product-list-component/locale';
import ProductLine from './components/product-line';
import ProductLineShimmer from './components/product-line-shimmer';
import { getBestUnitOfMeasure } from '../unit-of-measure/utils/humanize-unit-of-measure';

export interface IProductListCompactComponentProps {
    context: ICoreContext,
    moduleId: string;
    moduleType: string;
    resources: IProductListResources;
    mode?: 'add-to-cart' | 'update-cart';
    allowMultiSelect?: boolean;
    allowReorder?: boolean;
    alwaysBreakoff?: boolean;
    products: ProductListProduct[];
    simpleProducts?: SimpleProductExt[];
    customerInformation?: Customer;
    unitConversions?: UnitOfMeasureConversion[];
    wishlists?: CommerceList[];
    productErrors?: IProductError[];
    selectedProducts?: IProductSelection;
    breakoffProducts?: { [key: number]: boolean };
    min?: number;
    readOnly?: boolean;
    showGroups?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showHistoricalPrice?: boolean;
    validatedProductAccess?: ValidateProductAccessResult[];
    defaultWishlistName?: string;
    imageSettings?: IImageSettings;
    placeholderLines?: number;
    onBuyValidate?: ((product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>) | undefined;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (productRecId: number | undefined, targetRecId: number | undefined, direction: 'over' | 'under') => void;
    onProductRemove?: (product: ProductListProduct) => Promise<void>;
    onUpdateQuantity?: (product: ProductListProduct, quantity: number) => void;
    onUpdateSelection?: (selection: IProductSelection) => void;
    onUpdateOrderTemplates?: (orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
}

export interface ProductState {
    breakoff: boolean;
    quantity: number;
}

export interface ProductStates {
    [productRecordId: number]: ProductState;
}

function getSorter(showGroups: boolean) {
    return (a: ProductListProduct, b: ProductListProduct) => {
        if (showGroups) {
            // Sort any product without a RequestedDeliveryDate to the top
            if (!a.RequestedDeliveryDate && b.RequestedDeliveryDate) {
                return -1;
            }
            if (a.RequestedDeliveryDate && !b.RequestedDeliveryDate) {
                return 1;
            }

            if (a.RequestedDeliveryDate === b.RequestedDeliveryDate) {
                // Do no sorting
                return 0;
            }

            // Sort by date, first to last
            if (a.RequestedDeliveryDate && b.RequestedDeliveryDate) {
                return a.RequestedDeliveryDate < b.RequestedDeliveryDate ? -1 : 1;
            }
        }
        // Do no sorting
        return 0;
    };
}

function getInitialProductStates(products: ProductListProduct[], breakoffProducts: { [key: number]: boolean } | undefined): ProductStates {
    const productStates: ProductStates = {};
    products.forEach(product => {
        productStates[product.RecordId!] = {
            breakoff: !!breakoffProducts?.[product.RecordId!],
            quantity: product.CurrentQuantity || 0
        };
    });
    return productStates;
}

const ProductListCompactComponent = (props: IProductListCompactComponentProps) => {
    const {
        context,
        products,
        mode = 'add-to-cart'
    } = props;
    const baseClassName = 'product-list-compact-component';

    const [productStates, setProductStates] = React.useState<ProductStates>(getInitialProductStates(products, props.breakoffProducts));
    const [selectedProducts, setSelectedProducts] = React.useState<IProductSelection>(props.selectedProducts || {});
    const [dragOverProduct, setDragOverProduct] = React.useState<number | undefined>(undefined);
    const [dragOverUnder, setDragOverUnder] = React.useState<'over' | 'under' | undefined>(undefined);

    const cartQueue = React.useMemo(() => new PQueue({ concurrency: 1 }), []);
    const locale = React.useMemo(() => getLocale(context), []);
    const [showMoreIndex, setShowMoreIndex] = React.useState<number | undefined>(undefined);

    React.useEffect(() => {
        // Detect clicks outside element with class 'product-list-compact-component__more-popup'
        const handleClickOutside = (event: MouseEvent) => {
            if (showMoreIndex !== undefined) {
                const element = document.querySelector(`.${baseClassName}__more-popup`) as HTMLElement;
                if (element && !element.contains(event.target as Node)) {
                    setShowMoreIndex(undefined);
                }
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showMoreIndex]);

    React.useEffect(() => {
        props.onUpdateSelection?.(selectedProducts);
    }, [selectedProducts]);

    React.useEffect(() => {
        setSelectedProducts(props.selectedProducts || {});
    }, [props.selectedProducts]);

    const handleDrop = async (e: React.DragEvent<HTMLElement>, recordId: number | undefined, overUnder: "over" | "under" | undefined) => {
        const dragData = JSON.parse(e.dataTransfer.getData('product'));
        const dragProduct = products.find(product => product.RecordId === dragData.ProductRecordId);
        const targetProduct = products.find(product => product.RecordId === recordId);

        if (!dragProduct || !targetProduct || !overUnder) {
            return;
        }

        props.onDrop?.(dragProduct?.RecordId, targetProduct?.RecordId, overUnder);
    };
    const onBreakoffChange = (product: ProductListProduct, breakoff: boolean) => {
        setProductStates((curr) => {
            const newProductStates = { ...curr };
            if (!newProductStates[product.RecordId!]) {
                newProductStates[product.RecordId!] = { breakoff, quantity: 0 };
            } else {
                newProductStates[product.RecordId!].breakoff = breakoff;
            }
            return newProductStates;
        });
        props.onBreakoffChange?.(product, breakoff);
    };
    const onQuantityChange = (product: ProductListProduct, quantity: number) => {
        setProductStates((curr) => {
            const newProductStates = { ...curr };
            if (!newProductStates[product.RecordId!]) {
                newProductStates[product.RecordId!] = { breakoff: false, quantity };
            } else {
                newProductStates[product.RecordId!].quantity = quantity;
            }
            return newProductStates;
        });
        props.onUpdateQuantity?.(product, quantity);
    };
    const findContaingRectIndex = React.useCallback((rects: DOMRect[], x: number, y: number): number => {
        for (let i = 0; i < rects.length; i++) {
            const rect = rects[i];
            if (
                x >= rect.left &&
                x <= rect.right &&
                y >= rect.top &&
                y <= rect.bottom
            ) {
                return i;
            }
        }
        return -1;
    }, []);

    const requestedDeliveryDates = products.reduce((acc, product) => {
        let date = 'default';
        if (product.RequestedDeliveryDate) {
            date = new Date(product.RequestedDeliveryDate).getTime().toString();
            if (!acc.includes(date)) {
                acc.push(date);
            }
        } else if (!acc.includes(date)) {
            acc.push(date);
        }
        return acc;
    }, [] as string[]);
    const shouldShowGroups = requestedDeliveryDates.length > 1 && props.showGroups;

    products.sort(getSorter(!!props.showGroups));

    return (
        <div className={baseClassName}>
            <table
                className={`${baseClassName}__table`}
                onDrop={async e => {
                    await handleDrop(e, dragOverProduct, dragOverUnder);
                }}
                onDragOver={e => {
                    e.stopPropagation();

                    if (props.allowReorder && e.dataTransfer.types.includes('product')) {
                        e.preventDefault();

                        const table = document.querySelector(`.${baseClassName}__table`) as HTMLTableElement;
                        const rects = Array.from(table.rows).map(row => row.getBoundingClientRect());

                        const findRect = findContaingRectIndex(rects, e.clientX, e.clientY - 8);
                        if (findRect < 0) {
                            return;
                        }

                        const overOrUnder = e.clientY - rects[findRect].top < rects[findRect].height / 2 ? 'over' : 'under';
                        const item = table.rows[findRect];
                        const productId = item?.getAttribute('data-product-id') ? parseFloat(item.getAttribute('data-product-id')!) : undefined;

                        setDragOverProduct(productId);
                        setDragOverUnder(overOrUnder);
                    }
                }}
            >
                <thead>
                    <tr>
                        {(props.allowMultiSelect || props.allowReorder) && (
                            <th>#</th>
                        )}
                        <th></th> {/* Empty cell for product image */}
                        {/* <th>
                            <div>
                                <span>{locale.columns.producer}</span>
                                Icon for sorting. Not used for now.
                                <svg width="15px" height="15px" version="1.1" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
                                    <path d="m342.86 422.2 256.43 262.1 257.85-262.1c34.004-35.418 92.09 22.668 56.672 58.086l-290.44 298.94c-12.75 12.75-34.004 12.75-45.336 0l-293.27-298.94c-34.004-35.418 24.086-93.508 58.086-58.086z" fillRule="evenodd"/>
                                </svg>
                            </div>
                        </th> */}
                        <th>{locale.columns.productName}</th>
                        <th>{locale.columns.vintage}</th>
                        <th>{locale.columns.itemNumber}</th>
                        <th>{locale.columns.country}</th>
                        <th></th> {/* Empty cell for volume */}
                        <th></th> {/* Empty cell for quantity */}
                        <th></th> {/* Empty cell for quantity summary and price */}
                        {props.showTotalPrices && (
                            <th className='total-price'>
                                {locale.columns.totalPrice}
                            </th>
                        )}
                        {mode === 'add-to-cart' && (
                            <th></th> /* Empty cell for buy button */
                        )}
                        <th></th> {/* Empty cell for more options button */}
                    </tr>
                </thead>
                <tbody
                >
                    {props.placeholderLines && Array.from({ length: props.placeholderLines }).map((_, index) => (
                        <ProductLineShimmer key={index} className={baseClassName} />
                    ))}
                    {products.map((product, index) => {
                        let group: JSX.Element | undefined;

                        if (shouldShowGroups && product.RequestedDeliveryDate && requestedDeliveryDates.includes(new Date(product.RequestedDeliveryDate).getTime().toString())) {
                            group = (
                                <tr key={new Date(product.RequestedDeliveryDate).getTime()} className={`${baseClassName}__group-row`}>
                                    <td colSpan={12} className={`${baseClassName}__group`}>
                                        {props.resources.deliveryDateLabel}: {context.cultureFormatter.formatDate(new Date(product.RequestedDeliveryDate), { day: 'numeric', month: 'long' })}
                                    </td>
                                </tr>
                            );
                            requestedDeliveryDates.splice(requestedDeliveryDates.indexOf(new Date(product.RequestedDeliveryDate).getTime().toString()), 1);
                        }

                        let freeItem: JSX.Element | undefined;

                        if (!!product.FreeItemQuantity) {
                            const primaryImageSource = generateImageUrl(product.PrimaryImageUrl ?? '', context.request.apiSettings) ?? '';
                            const fallBackImg = getFallbackImageUrl(product.ItemId, context.request.apiSettings, context.request.app?.config?.OmniChannelMedia) ?? '';

                            // TEST: One cell spans entire row
                            freeItem = (
                                <tr key={`${product.RecordId}-free-item`} className={`${baseClassName}__free-item-row`}>
                                    <td colSpan={12} className={`${baseClassName}__free-item`}>
                                        <div style={{ display: 'flex', gap: 10 }}>
                                            <Image
                                                altText={product.Name ?? 'image'}
                                                requestContext={props.context.actionContext.requestContext}
                                                src={primaryImageSource}
                                                fallBackSrc={fallBackImg}
                                                className='thumbnail'
                                                gridSettings={context.request.gridSettings!}
                                                imageSettings={props.imageSettings}
                                                loadFailureBehavior='empty'
                                            />
                                            <div className={`${baseClassName}__free-item__description`}>
                                                <div className={`${baseClassName}__free-item__name`}>
                                                    {format((props.resources as any).freeItemText || '{0} · {1} {2}',
                                                        product.Name,
                                                        product.FreeItemQuantity,
                                                        getBestUnitOfMeasure(product, props.simpleProducts?.find(x => x.RecordId === product.RecordId), product.FreeItemQuantity)
                                                    )}
                                                </div>
                                                <div className={`${baseClassName}__free-item__description-text`}>
                                                    {format((props.resources as any).freeItemDescriptionText || 'Lagt til automatisk · Gratisvare', product.FreeItemQuantity)}
                                                </div>
                                            </div>
                                            <div className={`${baseClassName}__free-item__free-label`}>
                                                GRATIS
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            );
                        }

                        return (
                            <>
                            {group}
                            <ProductLine
                                key={product.RecordId}
                                {...props}
                                baseClassName={baseClassName}
                                locale={locale}
                                product={product}
                                isDragOver={props.allowReorder && product.RecordId === dragOverProduct ? dragOverUnder : undefined}
                                isSelected={selectedProducts[product.RecordId!] === true}
                                showMore={showMoreIndex === index}
                                productStates={productStates}
                                cartQueue={cartQueue}
                                error={props.productErrors?.find(x => x.RecordId === product.RecordId)}
                                setShowMore={(value) => setShowMoreIndex(value ? index : undefined)}
                                onBreakoffChange={onBreakoffChange}
                                onDragOver={(recordId, overUnder) => {
                                    setDragOverProduct(recordId);
                                    setDragOverUnder(overUnder);
                                }}
                                onDropLine={handleDrop}
                                onIsSelectedChange={(isSelected) => {
                                    setSelectedProducts((curr) => {
                                        const newSelectedProducts = { ...curr };
                                        if (isSelected) {
                                            newSelectedProducts[product.RecordId!] = true;
                                        } else {
                                            delete newSelectedProducts[product.RecordId!];
                                        }
                                        return newSelectedProducts;
                                    });
                                }}
                                onQuantityChange={onQuantityChange}
                                setProductStates={setProductStates}
                            />
                            {freeItem}
                            </>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ProductListCompactComponent;